/**
 * Reusable Solutions page template (BR-006 story arc for persona/strategy pages).
 * Sections: hero → pains → playbook → products-they-use → proof → related → CTA.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Play, Check, ChevronRight, ArrowUpRight, Sparkles, X } from "lucide-react";
import { MarketingHeader, MarketingFooter } from "./MarketingChrome";

const Kicker = ({ children }) => (
  <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-semibold">
    {children}
  </div>
);

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold text-white">
    {children}
  </span>
);

const Section = ({ children, bg = "white", tid }) => {
  const bgClass = bg === "navy"
    ? "bg-[#0B1F3A] text-white"
    : bg === "slate"
    ? "bg-slate-50 text-slate-900"
    : "bg-white text-slate-900";
  return (
    <section data-testid={tid} className={bgClass}>
      <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">{children}</div>
    </section>
  );
};

export default function SolutionPageTemplate({
  slug,
  persona,
  eyebrow,
  title,
  titleAccent,
  subtitle,
  bullets = [],
  metricStrip = [],
  pains = [],
  playbook = [],
  productsUsed = [],
  proof,
  related = [],
}) {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`solution-page-${slug}`}>
      <MarketingHeader />

      {/* HERO */}
      <section
        data-testid="solution-hero"
        className="relative overflow-hidden bg-[#0B1F3A] text-white"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(5,150,105,0.22),transparent_60%)]"></div>
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="max-w-3xl">
            <Pill>
              <Sparkles className="w-3 h-3 text-emerald-400" />
              {eyebrow}
            </Pill>
            <h1
              data-testid="solution-hero-headline"
              className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            >
              {title}
              {titleAccent && (
                <>
                  <br />
                  <span className="text-emerald-400">{titleAccent}</span>
                </>
              )}
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl">{subtitle}</p>
            {bullets.length > 0 && (
              <ul className="mt-6 space-y-2 text-sm text-slate-300">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/auth?mode=signup"
                data-testid="solution-cta-primary"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
              >
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="solution-cta-secondary"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/5 text-white font-semibold px-6 py-3"
              >
                <Play className="w-4 h-4" /> Book Live Demo
              </Link>
            </div>
            {persona && (
              <div className="mt-8 text-xs font-mono text-slate-400">
                Built for {persona}
              </div>
            )}
          </div>

          {metricStrip.length > 0 && (
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3">
              {metricStrip.map(({ stat, label }) => (
                <div key={label} className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <div className="text-2xl md:text-3xl font-bold text-emerald-400">{stat}</div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PAINS */}
      {pains.length > 0 && (
        <Section tid="solution-pains" bg="slate">
          <Kicker>The Pain</Kicker>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">
            Sound familiar?
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {pains.map(({ title: t, desc }) => (
              <div key={t} className="rounded-xl border border-slate-200 bg-white p-5 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                  <X className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-bold">{t}</div>
                  <div className="mt-1 text-sm text-slate-600">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* PLAYBOOK */}
      {playbook.length > 0 && (
        <Section tid="solution-playbook" bg="white">
          <Kicker>The Playbook</Kicker>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">
            How the platform solves it — step by step.
          </h2>
          <div className="mt-10 grid md:grid-cols-4 gap-4">
            {playbook.map(({ step, title: t, desc, bp }, i) => (
              <div key={t} className="relative rounded-xl border border-slate-200 p-5 hover:border-emerald-500 hover:shadow-lg transition-all">
                <div className="text-3xl font-bold text-emerald-600">
                  {String(step ?? i + 1).padStart(2, "0")}
                </div>
                <div className="mt-2 font-bold">{t}</div>
                <div className="mt-1 text-sm text-slate-600">{desc}</div>
                {bp && (
                  <div className="mt-3 text-[10px] font-mono text-emerald-600">{bp}</div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* PRODUCTS USED */}
      {productsUsed.length > 0 && (
        <Section tid="solution-products" bg="slate">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <Kicker>Products they lean on</Kicker>
              <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-2xl">
                Every engine, ranked for this strategy.
              </h2>
            </div>
            <Link
              to="/products"
              className="text-emerald-600 font-semibold flex items-center gap-1 hover:underline"
            >
              Explore all products <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {productsUsed.map(({ icon: Icon, title: t, desc, to }) => (
              <Link
                key={t}
                to={to}
                data-testid={`solution-product-${to.split("/").pop()}`}
                className="group rounded-xl bg-white border border-slate-200 p-5 hover:border-emerald-500 hover:shadow-lg transition-all"
              >
                {Icon && <Icon className="w-6 h-6 text-emerald-600" />}
                <div className="mt-3 font-bold text-lg group-hover:text-emerald-700">{t}</div>
                <div className="mt-2 text-sm text-slate-600">{desc}</div>
                <div className="mt-3 text-emerald-600 text-sm font-semibold inline-flex items-center gap-1">
                  Learn more <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* PROOF */}
      {proof && (
        <Section tid="solution-proof" bg="navy">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div>
              <div className="text-6xl md:text-7xl font-bold text-emerald-400">
                {proof.stat}
              </div>
              <div className="mt-2 text-sm uppercase tracking-widest text-slate-300">
                {proof.label}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-2xl md:text-3xl font-serif italic text-white leading-snug">
                &ldquo;{proof.quote}&rdquo;
              </div>
              <div className="mt-4 text-sm text-slate-400">— {proof.attribution}</div>
            </div>
          </div>
        </Section>
      )}

      {/* RELATED */}
      {related.length > 0 && (
        <Section tid="solution-related" bg="slate">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <Kicker>Explore other strategies</Kicker>
              <h2 className="mt-2 text-3xl md:text-4xl font-bold">
                Different playbook. Same platform.
              </h2>
            </div>
            <Link
              to="/solutions"
              className="text-emerald-600 font-semibold flex items-center gap-1 hover:underline"
            >
              All solutions <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {related.map(({ title: t, desc, to }) => (
              <Link
                key={t}
                to={to}
                data-testid={`related-${to.split("/").pop()}`}
                className="group rounded-xl border border-slate-200 bg-white p-5 hover:border-emerald-500 hover:shadow-lg transition-all"
              >
                <div className="font-bold text-lg group-hover:text-emerald-600">{t}</div>
                <div className="mt-2 text-sm text-slate-600">{desc}</div>
                <div className="mt-3 text-emerald-600 text-sm font-semibold inline-flex items-center gap-1">
                  Learn more <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* FINAL CTA */}
      <section
        data-testid="solution-final-cta"
        className="relative overflow-hidden bg-[#0B1F3A] text-white"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,150,105,0.22),transparent_70%)]"></div>
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            Your strategy.<br />
            <span className="text-emerald-400">One operating system.</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/auth?mode=signup"
              data-testid="solution-final-cta-primary"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/5 text-white font-semibold px-6 py-3"
            >
              Book a live demo
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
